import React, { useContext } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import {
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  CalendarMonth as CalendarIcon,
  Restaurant as RestaurantIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import Sidebar from './Sidebar';
import BottomNav from './BottomNav';
import { useIsMobile } from '../../hooks';
import { colors, mobile } from '../../styles/tokens';

/**
 * Configuration de la navigation Coach (Desktop - Sidebar)
 */
const coachNavItems = [
  {
    section: 'Principal',
    items: [
      { label: 'Dashboard', icon: DashboardIcon, path: '/coach' },
      { label: 'Clients', icon: PeopleIcon, path: '/coach/clients' },
      { label: 'Programmes', icon: CalendarIcon, path: '/coach/plans' },
      { label: 'Recettes', icon: RestaurantIcon, path: '/coach/recipes' },
    ],
  },
  {
    section: 'Compte',
    items: [
      { label: 'Profil', icon: PersonIcon, path: '/coach/profile' },
      { label: 'Deconnexion', icon: LogoutIcon, action: 'logout' },
    ],
  },
];

/**
 * Configuration de la navigation Coach (Mobile - Bottom Nav)
 * Maximum 5 items pour bottom nav
 */
const coachMobileNavItems = [
  { label: 'Accueil', icon: HomeIcon, path: '/coach' },
  { label: 'Clients', icon: PeopleIcon, path: '/coach/clients' },
  { label: 'Recettes', icon: RestaurantIcon, path: '/coach/recipes' },
  { label: 'Programmes', icon: CalendarIcon, path: '/coach/plans' },
  { label: 'Profil', icon: PersonIcon, path: '/coach/profile' },
];

/**
 * Layout Coach avec Sidebar (desktop) ou BottomNav (mobile)
 */
const CoachLayout = () => {
  const { logout, user } = useContext(AuthContext);
  const { isMobileOrTablet } = useIsMobile();
  const location = useLocation();

  // Determine le titre de la page pour le header mobile
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/coach') return 'Dashboard';
    if (path.startsWith('/coach/clients')) return 'Clients';
    if (path.startsWith('/coach/plans')) return 'Programmes';
    if (path.startsWith('/coach/recipes')) return 'Recettes';
    if (path.startsWith('/coach/profile')) return 'Profil';
    return 'Coach';
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar - Desktop only */}
      {!isMobileOrTablet && (
        <Sidebar
          navItems={coachNavItems}
          title="Coach"
          onLogout={logout}
          user={user}
        />
      )}

      {/* Contenu principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          // IMPORTANT: Eviter debordement horizontal
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          bgcolor: colors.bgPage,
          // Mobile: padding bottom pour bottom nav + safe area
          pb: isMobileOrTablet ? `calc(${mobile.bottomNavHeight}px + ${mobile.safeAreaBottom} + 16px)` : 0,
        }}
      >
        {/* Zone de contenu */}
        <Box
          sx={{
            // Padding responsive uniforme
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 3 },
            // Mobile: safe area top
            pt: isMobileOrTablet ? `calc(${mobile.safeAreaTop} + 16px)` : { xs: 2, sm: 3 },
            // Eviter debordement
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: '100%',
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Bottom Navigation - Mobile only */}
      {isMobileOrTablet && (
        <BottomNav navItems={coachMobileNavItems} />
      )}
    </Box>
  );
};

export default CoachLayout;
